import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, Users, MessageSquare, FileText, 
  LogOut, Plus, Send, Download, Eye, Check, X,
  TrendingUp, Clock, DollarSign, Activity, CreditCard, Printer, Edit2, Save,
  Search, Mail, RefreshCw
} from 'lucide-react';

const AdminPanel = () => {
  const [currentView, setCurrentView] = useState('login');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [adminUser, setAdminUser] = useState(null);
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [invoiceSummary, setInvoiceSummary] = useState({ count: 0, totalAmount: 0, totalInvoices: 0 });
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingQuote, setEditingQuote] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showStandaloneQuoteModal, setShowStandaloneQuoteModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAccountSummaryModal, setShowAccountSummaryModal] = useState(false);
  const [selectedCustomerForPayment, setSelectedCustomerForPayment] = useState(null);
  const [selectedCustomerForStatement, setSelectedCustomerForStatement] = useState(null);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API_URL}/admin/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  const fetchAllData = async () => {
    const token = localStorage.getItem('adminToken');
    
    try {
      const [usersRes, enquiriesRes, quotesRes, unpaidRes] = await Promise.all([
        fetch(`${API_URL}/admin/users`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${API_URL}/admin/enquiries`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${API_URL}/admin/quotes`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${API_URL}/admin/unpaid-invoices`, { headers: { 'Authorization': `Bearer ${token}` }})
      ]);

      if (usersRes.ok) setUsers(await usersRes.json());
      if (enquiriesRes.ok) setEnquiries(await enquiriesRes.json());
      if (quotesRes.ok) setQuotes(await quotesRes.json());
      if (unpaidRes.ok) {
        const unpaidData = await unpaidRes.json();
        setUnpaidInvoices(unpaidData.customers || []);
        setInvoiceSummary(unpaidData.summary || { count: 0, totalAmount: 0, totalInvoices: 0 });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    if (currentView === 'dashboard') {
      fetchDashboardData();
      fetchAllData();
    }
  }, [currentView]);

  const handleRespond = async (enquiryId) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API_URL}/admin/enquiries/${enquiryId}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ response: responseText })
      });

      if (response.ok) {
        alert('Response sent successfully!');
        setRespondingTo(null);
        setResponseText('');
        fetchAllData();
      }
    } catch (error) {
      console.error('Error responding:', error);
    }
  };

  const markAsPaid = async (quoteId) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API_URL}/admin/quotes/${quoteId}/mark-paid`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Invoice marked as paid');
        fetchAllData();
      }
    } catch (error) {
      console.error('Error marking as paid:', error);
      alert('Failed to mark as paid');
    }
  };

  const resendInvoice = async (quoteId) => {
    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API_URL}/admin/quotes/${quoteId}/resend-invoice`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Invoice resent successfully!');
      } else {
        alert('Failed to resend invoice');
      }
    } catch (error) {
      console.error('Error resending invoice:', error);
      alert('Failed to resend invoice');
    }
  };

  const printUnpaidSummary = () => {
    const printWindow = window.open('', '_blank');
    const currentDate = new Date().toLocaleDateString();
    
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Outstanding Accounts Summary</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .summary { background: #f5f5f5; padding: 15px; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .total { font-weight: bold; background-color: #e8f5e8; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Newgate Nurseries Ltd</h1>
            <h2>Outstanding Accounts Summary</h2>
            <p>Generated on: ${currentDate}</p>
          </div>
          
          <div class="summary">
            <h3>Summary</h3>
            <p><strong>Total Customers with Outstanding Balance:</strong> ${invoiceSummary.count || 0}</p>
            <p><strong>Total Outstanding Invoices:</strong> ${invoiceSummary.totalInvoices || 0}</p>
            <p><strong>Total Outstanding Amount:</strong> £${(invoiceSummary.totalAmount || 0).toFixed(2)}</p>
          </div>

          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Contact</th>
                <th class="text-center">Invoices</th>
                <th class="text-right">Total Invoiced</th>
                <th class="text-right">Total Paid</th>
                <th class="text-right">Outstanding</th>
                <th class="text-center">Days</th>
              </tr>
            </thead>
            <tbody>
              ${unpaidInvoices.map(customer => {
                const totalInvoiced = customer.total_invoiced || 0;
                const outstanding = customer.outstanding || 0;
                const totalPaid = customer.total_paid !== undefined ? customer.total_paid : (totalInvoiced - outstanding);
                return `
                <tr>
                  <td>${customer.business_name}</td>
                  <td>${customer.contact_name}</td>
                  <td class="text-center">${customer.invoice_count || 0}</td>
                  <td class="text-right">£${totalInvoiced.toFixed(2)}</td>
                  <td class="text-right">£${totalPaid.toFixed(2)}</td>
                  <td class="text-right">£${outstanding.toFixed(2)}</td>
                  <td class="text-center">${customer.days_outstanding || 0}</td>
                </tr>
              `;}).join('')}
            </tbody>
            <tfoot>
              <tr class="total">
                <td colspan="5"><strong>Total Outstanding</strong></td>
                <td class="text-right"><strong>£${(invoiceSummary.totalAmount || 0).toFixed(2)}</strong></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
          
          <div style="margin-top: 30px; font-size: 12px; color: #666;">
            <p><strong>Note:</strong> Days outstanding calculated from oldest invoice date per customer.</p>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  // Customer Search Component
  const CustomerSearch = ({ onSelect, placeholder = "Search customers..." }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    const handleSearch = async (term) => {
      setSearchTerm(term);
      
      if (term.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      const token = localStorage.getItem('adminToken');
      
      try {
        const response = await fetch(`${API_URL}/admin/customers/search?q=${encodeURIComponent(term)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const results = await response.json();
          setSearchResults(results);
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    };

    return (
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={placeholder}
            className="w-full pl-10 pr-3 py-2 border rounded-lg"
          />
        </div>
        
        {searchResults.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {searchResults.map(customer => (
              <button
                key={customer.id}
                onClick={() => {
                  onSelect(customer);
                  setSearchTerm('');
                  setSearchResults([]);
                }}
                className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b last:border-b-0"
              >
                <div className="font-medium">{customer.business_name}</div>
                <div className="text-sm text-gray-600">{customer.contact_name} - {customer.email}</div>
              </button>
            ))}
          </div>
        )}
        
        {isSearching && (
          <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg p-4 text-center text-gray-500">
            Searching...
          </div>
        )}
      </div>
    );
  };

  // Bulk Email Tab Component
  const BulkEmailTab = () => {
    const [templates, setTemplates] = useState([]);
    const [recipients, setRecipients] = useState([]);
    const [recipientCount, setRecipientCount] = useState(0);
    const [logoUrl, setLogoUrl] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [emailSubject, setEmailSubject] = useState('');
    const [editorMode, setEditorMode] = useState('easy'); // 'easy' or 'html'
    const [htmlBody, setHtmlBody] = useState('');
    
    // Easy editor fields
    const [editorData, setEditorData] = useState({
      headline: '',
      subheadline: '',
      introText: '',
      mainContent: '',
      highlightBox: '',
      bulletPoints: ['', '', ''],
      ctaButton: '',
      ctaUrl: 'https://www.newgatenurseriestrade.com',
      closingText: '',
      signatureName: 'The Newgate Nurseries Team'
    });
    
    // Attachments
    const [attachments, setAttachments] = useState([]);
    
    const [testEmail, setTestEmail] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [sendResults, setSendResults] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [loadingRecipients, setLoadingRecipients] = useState(true);

    useEffect(() => {
      fetchTemplates();
      fetchRecipients();
    }, []);

    const fetchTemplates = async () => {
      const token = localStorage.getItem('adminToken');
      try {
        const response = await fetch(`${API_URL}/admin/bulk-email/templates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setTemplates(data.templates || []);
          setLogoUrl(data.logoUrl || '');
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };

    const fetchRecipients = async () => {
      const token = localStorage.getItem('adminToken');
      try {
        const response = await fetch(`${API_URL}/admin/bulk-email/recipients`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setRecipients(data.recipients);
          setRecipientCount(data.count);
          if (data.logoUrl) setLogoUrl(data.logoUrl);
        }
      } catch (error) {
        console.error('Error fetching recipients:', error);
      } finally {
        setLoadingRecipients(false);
      }
    };

    const handleTemplateSelect = (template) => {
      setSelectedTemplate(template);
      setEmailSubject(template.subject);
      if (template.editorData) {
        setEditorData({
          ...editorData,
          ...template.editorData,
          bulletPoints: template.editorData.bulletPoints || ['', '', '']
        });
        setEditorMode('easy');
      }
      if (template.body) {
        setHtmlBody(template.body);
      }
    };

    const updateBulletPoint = (index, value) => {
      const newBullets = [...editorData.bulletPoints];
      newBullets[index] = value;
      setEditorData({ ...editorData, bulletPoints: newBullets });
    };

    const addBulletPoint = () => {
      setEditorData({ 
        ...editorData, 
        bulletPoints: [...editorData.bulletPoints, ''] 
      });
    };

    const removeBulletPoint = (index) => {
      const newBullets = editorData.bulletPoints.filter((_, i) => i !== index);
      setEditorData({ ...editorData, bulletPoints: newBullets });
    };

    const handleFileUpload = async (e) => {
      const files = Array.from(e.target.files);
      
      for (const file of files) {
        if (file.type !== 'application/pdf') {
          alert('Only PDF files are allowed');
          continue;
        }
        
        if (file.size > 5 * 1024 * 1024) {
          alert('File size must be less than 5MB');
          continue;
        }
        
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64 = event.target.result.split(',')[1];
          setAttachments(prev => [...prev, {
            filename: file.name,
            content: base64,
            type: 'application/pdf',
            size: file.size
          }]);
        };
        reader.readAsDataURL(file);
      }
      
      e.target.value = '';
    };

    const removeAttachment = (index) => {
      setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const generatePreview = async () => {
      const token = localStorage.getItem('adminToken');
      
      if (editorMode === 'easy') {
        try {
          const response = await fetch(`${API_URL}/admin/bulk-email/generate-html`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ...editorData,
              logoUrl: logoUrl
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            setPreviewHtml(data.html
              .replace(/{{contact_name}}/g, 'John Smith')
              .replace(/{{business_name}}/g, 'Example Garden Centre')
              .replace(/{{email}}/g, 'john@example.com')
            );
          }
        } catch (error) {
          console.error('Error generating preview:', error);
        }
      } else {
        setPreviewHtml(htmlBody
          .replace(/{{contact_name}}/g, 'John Smith')
          .replace(/{{business_name}}/g, 'Example Garden Centre')
          .replace(/{{email}}/g, 'john@example.com')
        );
      }
      
      setShowPreview(true);
    };

    const handleSendTest = async () => {
      if (!testEmail || !emailSubject) {
        alert('Please enter a test email address and subject');
        return;
      }

      setIsSending(true);
      setSendResults(null);
      const token = localStorage.getItem('adminToken');

      try {
        const payload = {
          subject: emailSubject,
          testMode: true,
          testEmail: testEmail,
          attachments: attachments
        };

        if (editorMode === 'easy') {
          payload.useEasyEditor = true;
          payload.emailData = { ...editorData, logoUrl };
        } else {
          payload.htmlBody = htmlBody;
        }

        const response = await fetch(`${API_URL}/admin/bulk-email/send`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        const result = await response.json();
        if (result.success) {
          alert('Test email sent successfully! Check your inbox.');
        } else {
          alert('Failed to send test email: ' + (result.error || 'Unknown error'));
        }
      } catch (error) {
        alert('Error sending test email: ' + error.message);
      } finally {
        setIsSending(false);
      }
    };

    const handleSendBulk = async () => {
      if (!emailSubject) {
        alert('Please fill in the subject');
        return;
      }

      if (!window.confirm(`Are you sure you want to send this email to ${recipientCount} customers?\n\nThis cannot be undone.`)) {
        return;
      }

      setIsSending(true);
      setSendResults(null);
      const token = localStorage.getItem('adminToken');

      try {
        const payload = {
          subject: emailSubject,
          testMode: false,
          attachments: attachments
        };

        if (editorMode === 'easy') {
          payload.useEasyEditor = true;
          payload.emailData = { ...editorData, logoUrl };
        } else {
          payload.htmlBody = htmlBody;
        }

        const response = await fetch(`${API_URL}/admin/bulk-email/send`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        const result = await response.json();
        setSendResults(result);
        
        if (result.success) {
          alert(`Bulk email completed!\n\nSent: ${result.results.sent}\nFailed: ${result.results.failed}`);
        } else {
          alert('Failed to send bulk email: ' + (result.error || 'Unknown error'));
        }
      } catch (error) {
        alert('Error sending bulk email: ' + error.message);
      } finally {
        setIsSending(false);
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Bulk Email Campaign</h3>
          <div className="flex items-center gap-4">
            {logoUrl && (
              <img src={logoUrl} alt="Logo" className="h-8 object-contain" />
            )}
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-600">
                {loadingRecipients ? 'Loading...' : `${recipientCount} recipients`}
              </span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-600">Total Customers</p>
            <p className="text-2xl font-bold text-blue-900">{recipientCount}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-green-600">Templates Available</p>
            <p className="text-2xl font-bold text-green-900">{templates.length}</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <p className="text-sm text-purple-600">Attachments</p>
            <p className="text-2xl font-bold text-purple-900">{attachments.length}</p>
          </div>
        </div>

        {/* Template Selection */}
        <div className="bg-white border rounded-lg p-4">
          <h4 className="font-medium mb-3">1. Choose a Template (Optional)</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {loadingTemplates ? (
              <p className="col-span-full text-gray-500">Loading templates...</p>
            ) : (
              templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleTemplateSelect(template)}
                  className={`p-3 border rounded-lg text-left hover:border-green-500 transition-colors ${
                    selectedTemplate?.id === template.id ? 'border-green-500 bg-green-50' : ''
                  }`}
                >
                  <p className="font-medium text-sm">{template.name}</p>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Email Composer */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-medium">2. Compose Your Email</h4>
            <div className="flex gap-2">
              <button
                onClick={() => setEditorMode('easy')}
                className={`px-3 py-1 text-sm rounded ${editorMode === 'easy' ? 'bg-green-600 text-white' : 'bg-gray-200'}`}
              >
                Easy Editor
              </button>
              <button
                onClick={() => setEditorMode('html')}
                className={`px-3 py-1 text-sm rounded ${editorMode === 'html' ? 'bg-green-600 text-white' : 'bg-gray-200'}`}
              >
                HTML Mode
              </button>
            </div>
          </div>
          
          {/* Subject Line */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line *</label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="Enter email subject..."
            />
          </div>

          {editorMode === 'easy' ? (
            <div className="space-y-4">
              {/* Header Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-sm text-gray-700 mb-3">📌 Header Section</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Headline</label>
                    <input
                      type="text"
                      value={editorData.headline}
                      onChange={(e) => setEditorData({...editorData, headline: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Main headline..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Subheadline (optional)</label>
                    <input
                      type="text"
                      value={editorData.subheadline}
                      onChange={(e) => setEditorData({...editorData, subheadline: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Subheadline..."
                    />
                  </div>
                </div>
              </div>

              {/* Content Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-sm text-gray-700 mb-3">📝 Content</h5>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Introduction</label>
                    <textarea
                      value={editorData.introText}
                      onChange={(e) => setEditorData({...editorData, introText: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Opening paragraph..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Main Content</label>
                    <textarea
                      value={editorData.mainContent}
                      onChange={(e) => setEditorData({...editorData, mainContent: e.target.value})}
                      rows={4}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Your main message... (HTML allowed)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Highlight Box (optional)</label>
                    <input
                      type="text"
                      value={editorData.highlightBox}
                      onChange={(e) => setEditorData({...editorData, highlightBox: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg border-yellow-400 bg-yellow-50"
                      placeholder="Special offer or important message..."
                    />
                  </div>
                </div>
              </div>

              {/* Bullet Points */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-sm text-gray-700 mb-3">📋 Bullet Points (optional)</h5>
                <div className="space-y-2">
                  {editorData.bulletPoints.map((point, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={point}
                        onChange={(e) => updateBulletPoint(index, e.target.value)}
                        className="flex-1 px-3 py-2 border rounded-lg"
                        placeholder={`Bullet point ${index + 1}...`}
                      />
                      <button
                        onClick={() => removeBulletPoint(index)}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={addBulletPoint}
                    className="text-green-600 text-sm hover:underline"
                  >
                    + Add bullet point
                  </button>
                </div>
              </div>

              {/* Call to Action */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-sm text-gray-700 mb-3">🔗 Call to Action Button (optional)</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Button Text</label>
                    <input
                      type="text"
                      value={editorData.ctaButton}
                      onChange={(e) => setEditorData({...editorData, ctaButton: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="e.g., View Our Stock"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Button URL</label>
                    <input
                      type="url"
                      value={editorData.ctaUrl}
                      onChange={(e) => setEditorData({...editorData, ctaUrl: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>

              {/* Closing */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-sm text-gray-700 mb-3">👋 Closing</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Closing Text</label>
                    <textarea
                      value={editorData.closingText}
                      onChange={(e) => setEditorData({...editorData, closingText: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Final message before signature..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Signature Name</label>
                    <input
                      type="text"
                      value={editorData.signatureName}
                      onChange={(e) => setEditorData({...editorData, signatureName: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="The Newgate Nurseries Team"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                HTML Body
                <span className="text-gray-400 ml-2">
                  Use {'{{contact_name}}'}, {'{{business_name}}'}, {'{{email}}'} for personalization
                </span>
              </label>
              <textarea
                value={htmlBody}
                onChange={(e) => setHtmlBody(e.target.value)}
                rows={15}
                className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
                placeholder="Enter HTML email body..."
              />
            </div>
          )}

          <button
            onClick={generatePreview}
            className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            Preview Email
          </button>
        </div>

        {/* Attachments */}
        <div className="bg-white border rounded-lg p-4">
          <h4 className="font-medium mb-3">3. Attach Documents (Optional)</h4>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              accept=".pdf"
              multiple
              onChange={handleFileUpload}
              className="hidden"
              id="pdf-upload"
            />
            <label htmlFor="pdf-upload" className="cursor-pointer">
              <div className="text-gray-500">
                <FileText className="w-10 h-10 mx-auto mb-2 text-gray-400" />
                <p className="font-medium">Click to upload PDF documents</p>
                <p className="text-sm">Max 5MB per file</p>
              </div>
            </label>
          </div>

          {attachments.length > 0 && (
            <div className="mt-4 space-y-2">
              {attachments.map((att, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-red-500" />
                    <div>
                      <p className="font-medium text-sm">{att.filename}</p>
                      <p className="text-xs text-gray-500">{(att.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeAttachment(index)}
                    className="text-red-600 hover:bg-red-50 p-2 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Test & Send */}
        <div className="bg-white border rounded-lg p-4">
          <h4 className="font-medium mb-3">4. Test & Send</h4>
          
          <div className="space-y-4">
            {/* Test Email */}
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Send Test Email To</label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="your@email.com"
                />
              </div>
              <button
                onClick={handleSendTest}
                disabled={isSending || !testEmail}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Send Test
              </button>
            </div>

            {/* Bulk Send */}
            <div className="border-t pt-4 mt-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-yellow-800 font-medium">⚠️ Bulk Email Warning</p>
                <p className="text-yellow-700 text-sm mt-1">
                  This will send an email to all {recipientCount} trade customers
                  {attachments.length > 0 && ` with ${attachments.length} attachment(s)`}. 
                  Emails will be sent in batches of 50 with a 2-second delay between batches.
                </p>
              </div>

              <button
                onClick={handleSendBulk}
                disabled={isSending || !emailSubject}
                className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
              >
                {isSending ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Sending... Please wait
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5" />
                    Send to All {recipientCount} Customers
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        {sendResults && (
          <div className={`border rounded-lg p-4 ${sendResults.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <h4 className={`font-medium mb-2 ${sendResults.success ? 'text-green-800' : 'text-red-800'}`}>
              {sendResults.success ? '✅ Campaign Complete' : '❌ Campaign Failed'}
            </h4>
            {sendResults.results && (
              <div className="grid grid-cols-3 gap-4 mt-3">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{sendResults.results.total}</p>
                  <p className="text-sm text-gray-600">Total</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{sendResults.results.sent}</p>
                  <p className="text-sm text-gray-600">Sent</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{sendResults.results.failed}</p>
                  <p className="text-sm text-gray-600">Failed</p>
                </div>
              </div>
            )}
            {sendResults.results?.errors?.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-red-800 mb-2">Failed Emails:</p>
                <div className="max-h-32 overflow-y-auto text-sm text-red-700">
                  {sendResults.results.errors.map((err, idx) => (
                    <p key={idx}>{err.email}: {err.error}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-semibold">Email Preview</h3>
                <button onClick={() => setShowPreview(false)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 border-b bg-gray-50">
                <p className="text-sm"><strong>Subject:</strong> {emailSubject}</p>
                {attachments.length > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Attachments:</strong> {attachments.map(a => a.filename).join(', ')}
                  </p>
                )}
              </div>
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              </div>
              <div className="p-4 border-t bg-gray-50">
                <button
                  onClick={() => setShowPreview(false)}
                  className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Recipients List */}
        <div className="bg-white border rounded-lg p-4">
          <h4 className="font-medium mb-3">Recipients ({recipientCount})</h4>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left py-2 px-3">Business</th>
                  <th className="text-left py-2 px-3">Contact</th>
                  <th className="text-left py-2 px-3">Email</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map((r) => (
                  <tr key={r.id} className="border-b">
                    <td className="py-2 px-3">{r.business_name}</td>
                    <td className="py-2 px-3">{r.contact_name}</td>
                    <td className="py-2 px-3 text-gray-600">{r.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Customer Modal
  const CustomerModal = ({ onClose, onSuccess }) => {
    const [customerData, setCustomerData] = useState({
      businessName: '',
      contactName: '',
      email: '',
      phone: '',
      password: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
      const token = localStorage.getItem('adminToken');
      setIsSubmitting(true);
      
      try {
        const response = await fetch(`${API_URL}/admin/customers/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(customerData)
        });

        if (response.ok) {
          alert('Customer created successfully!');
          onSuccess();
        } else {
          const errorData = await response.json();
          alert(errorData.error || 'Failed to create customer');
        }
      } catch (error) {
        console.error('Error creating customer:', error);
        alert('Failed to create customer');
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-6 max-w-md w-full">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold">Add Customer</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
              <input
                type="text"
                value={customerData.businessName}
                onChange={(e) => setCustomerData({...customerData, businessName: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
              <input
                type="text"
                value={customerData.contactName}
                onChange={(e) => setCustomerData({...customerData, contactName: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={customerData.email}
                onChange={(e) => setCustomerData({...customerData, email: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={customerData.phone}
                onChange={(e) => setCustomerData({...customerData, phone: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={customerData.password}
                onChange={(e) => setCustomerData({...customerData, password: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Creating...' : 'Create Customer'}
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Edit Customer Modal
  const EditCustomerModal = ({ customer, onClose, onSuccess }) => {
    const [customerData, setCustomerData] = useState({
      businessName: customer.business_name || '',
      contactName: customer.contact_name || '',
      email: customer.email || '',
      phone: customer.phone || ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
      const token = localStorage.getItem('adminToken');
      setIsSubmitting(true);
      
      try {
        const response = await fetch(`${API_URL}/admin/customers/${customer.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(customerData)
        });

        if (response.ok) {
          alert('Customer updated successfully!');
          onSuccess();
        } else {
          const errorData = await response.json();
          alert(errorData.error || 'Failed to update customer');
        }
      } catch (error) {
        console.error('Error updating customer:', error);
        alert('Failed to update customer');
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-6 max-w-md w-full">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold">Edit Customer</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
              <input
                type="text"
                value={customerData.businessName}
                onChange={(e) => setCustomerData({...customerData, businessName: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
              <input
                type="text"
                value={customerData.contactName}
                onChange={(e) => setCustomerData({...customerData, contactName: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={customerData.email}
                onChange={(e) => setCustomerData({...customerData, email: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={customerData.phone}
                onChange={(e) => setCustomerData({...customerData, phone: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isSubmitting ? 'Updating...' : 'Update Customer'}
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Payment Modal Component
  const PaymentModal = ({ customer, onClose, onSuccess }) => {
    const [paymentData, setPaymentData] = useState({
      quoteId: '',
      amount: '',
      paymentMethod: 'bank_transfer',
      notes: ''
    });
    const [customerInvoices, setCustomerInvoices] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
      if (customer) {
        const invoices = quotes.filter(q => 
          q.user_id === customer.id && 
          q.status === 'signed' && 
          q.payment_status !== 'paid'
        );
        setCustomerInvoices(invoices);
      }
    }, [customer]);

    const handleSubmit = async () => {
      const token = localStorage.getItem('adminToken');
      setIsSubmitting(true);

      try {
        const response = await fetch(`${API_URL}/admin/payments/add`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            userId: customer.id,
            quoteId: paymentData.quoteId,
            amount: parseFloat(paymentData.amount),
            paymentMethod: paymentData.paymentMethod,
            notes: paymentData.notes
          })
        });

        if (response.ok) {
          alert('Payment recorded successfully!');
          onSuccess();
        } else {
          const errorData = await response.json();
          alert(errorData.error || 'Failed to record payment');
        }
      } catch (error) {
        console.error('Error recording payment:', error);
        alert('Failed to record payment');
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-6 max-w-md w-full">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-xl font-bold">Record Payment</h3>
              <p className="text-sm text-gray-600">{customer.business_name}</p>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice</label>
              <select
                value={paymentData.quoteId}
                onChange={(e) => setPaymentData({...paymentData, quoteId: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">Select invoice...</option>
                {customerInvoices.map(inv => (
                  <option key={inv.id} value={inv.id}>
                    Invoice #{inv.id} - {inv.title} (£{parseFloat(inv.total).toFixed(2)})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
              <input
                type="number"
                step="0.01"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
              <select
                value={paymentData.paymentMethod}
                onChange={(e) => setPaymentData({...paymentData, paymentMethod: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="card">Card</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={paymentData.notes}
                onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Payment reference, notes..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !paymentData.quoteId || !paymentData.amount}
                className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Recording...' : 'Record Payment'}
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Account Summary Modal
  const AccountSummaryModal = ({ customer, onClose }) => {
    const [accountData, setAccountData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSendingStatement, setIsSendingStatement] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
      const fetchAccountSummary = async () => {
        const token = localStorage.getItem('adminToken');
        try {
          const response = await fetch(`${API_URL}/admin/customers/${customer.id}/account-summary`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log('Account summary data:', data);
            setAccountData(data);
          } else {
            const errorData = await response.json();
            setError(errorData.error || 'Failed to load account summary');
          }
        } catch (error) {
          console.error('Error fetching account summary:', error);
          setError('Failed to load account summary: ' + error.message);
        } finally {
          setIsLoading(false);
        }
      };

      fetchAccountSummary();
    }, [customer]);

    const sendStatement = async () => {
      const token = localStorage.getItem('adminToken');
      setIsSendingStatement(true);

      try {
        const now = new Date();
        const threeMonthsAgo = new Date(now.setMonth(now.getMonth() - 3));

        const response = await fetch(`${API_URL}/admin/customers/${customer.id}/send-statement`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            periodStart: threeMonthsAgo.toISOString(),
            periodEnd: new Date().toISOString()
          })
        });

        if (response.ok) {
          alert('Account statement sent successfully!');
        } else {
          alert('Failed to send statement');
        }
      } catch (error) {
        console.error('Error sending statement:', error);
        alert('Failed to send statement');
      } finally {
        setIsSendingStatement(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-xl font-bold">Account Summary</h3>
              <p className="text-sm text-gray-600">{customer.business_name}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={sendStatement}
                disabled={isSendingStatement}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
              >
                <Mail className="w-4 h-4" />
                {isSendingStatement ? 'Sending...' : 'Email Statement'}
              </button>
              <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : accountData ? (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Total Invoiced</p>
                  <p className="text-2xl font-bold text-blue-900">
                    £{(accountData.summary?.totalInvoiced || 0).toFixed(2)}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-600 mb-1">Total Paid</p>
                  <p className="text-2xl font-bold text-green-900">
                    £{(accountData.summary?.totalPaid || 0).toFixed(2)}
                  </p>
                </div>
                <div className={`p-4 rounded-lg ${(accountData.summary?.balance || 0) > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                  <p className={`text-sm mb-1 ${(accountData.summary?.balance || 0) > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                    Outstanding Balance
                  </p>
                  <p className={`text-2xl font-bold ${(accountData.summary?.balance || 0) > 0 ? 'text-red-900' : 'text-gray-900'}`}>
                    £{(accountData.summary?.balance || 0).toFixed(2)}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Invoices</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm">Invoice #</th>
                        <th className="px-4 py-2 text-left text-sm">Date</th>
                        <th className="px-4 py-2 text-left text-sm">Description</th>
                        <th className="px-4 py-2 text-right text-sm">Amount</th>
                        <th className="px-4 py-2 text-right text-sm">Paid</th>
                        <th className="px-4 py-2 text-right text-sm">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accountData.invoices.map(invoice => (
                        <tr key={invoice.id} className="border-t">
                          <td className="px-4 py-2">#{invoice.id}</td>
                          <td className="px-4 py-2">{new Date(invoice.signed_date).toLocaleDateString()}</td>
                          <td className="px-4 py-2">{invoice.title}</td>
                          <td className="px-4 py-2 text-right">£{parseFloat(invoice.total).toFixed(2)}</td>
                          <td className="px-4 py-2 text-right text-green-600">
                            £{parseFloat(invoice.amount_paid).toFixed(2)}
                          </td>
                          <td className="px-4 py-2 text-right font-medium">
                            £{(parseFloat(invoice.total) - parseFloat(invoice.amount_paid)).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {accountData.payments.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Payment History</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm">Date</th>
                          <th className="px-4 py-2 text-left text-sm">Invoice</th>
                          <th className="px-4 py-2 text-left text-sm">Method</th>
                          <th className="px-4 py-2 text-right text-sm">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accountData.payments.map(payment => (
                          <tr key={payment.id} className="border-t">
                            <td className="px-4 py-2">{new Date(payment.payment_date).toLocaleDateString()}</td>
                            <td className="px-4 py-2">
                              {payment.quote_title ? `#${payment.quote_id} - ${payment.quote_title}` : 'General Payment'}
                            </td>
                            <td className="px-4 py-2 capitalize">{payment.payment_method.replace('_', ' ')}</td>
                            <td className="px-4 py-2 text-right text-green-600">
                              £{parseFloat(payment.amount).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">Failed to load account summary</div>
          )}
        </div>
      </div>
    );
  };

  // Enhanced Quote Modal with VAT handling
  const EnhancedQuoteModal = ({ enquiry, users, onClose, onSuccess, standalone = false }) => {
    const [quoteData, setQuoteData] = useState({
      userId: enquiry ? enquiry.user_id : '',
      title: enquiry ? `Quote for ${enquiry.title}` : '',
      items: [{ name: '', quantity: 1, unitPrice: 0, vatExempt: false }],
      notes: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const addItem = () => {
      setQuoteData({
        ...quoteData,
        items: [...quoteData.items, { name: '', quantity: 1, unitPrice: 0, vatExempt: false }]
      });
    };

    const updateItem = (index, field, value) => {
      const newItems = [...quoteData.items];
      if (field === 'vatExempt') {
        newItems[index][field] = value;
      } else {
        newItems[index][field] = field === 'quantity' || field === 'unitPrice' ? parseFloat(value) || 0 : value;
      }
      setQuoteData({ ...quoteData, items: newItems });
    };

    const removeItem = (index) => {
      setQuoteData({
        ...quoteData,
        items: quoteData.items.filter((_, i) => i !== index)
      });
    };

    const calculateTotal = () => {
      let subtotal = 0;
      let vat = 0;
      
      quoteData.items.forEach(item => {
        const itemTotal = parseFloat(item.quantity) * parseFloat(item.unitPrice || 0);
        subtotal += itemTotal;
        if (!item.vatExempt) {
          vat += itemTotal * 0.20;
        }
      });
      
      return { subtotal, vat, total: subtotal + vat };
    };

    const handleSubmit = async () => {
      const token = localStorage.getItem('adminToken');
      setIsSubmitting(true);

      const endpoint = standalone 
        ? `${API_URL}/admin/quotes/create-standalone`
        : `${API_URL}/admin/quotes/create`;

      const payload = standalone
        ? { ...quoteData }
        : { ...quoteData, enquiryId: enquiry.id };

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          alert('Quote created and sent successfully!');
          onSuccess();
        } else {
          const errorData = await response.json();
          alert(errorData.error || 'Failed to create quote');
        }
      } catch (error) {
        console.error('Error creating quote:', error);
        alert('Failed to create quote');
      } finally {
        setIsSubmitting(false);
      }
    };

    const totals = calculateTotal();

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold">
              {standalone ? 'Create Standalone Quote' : 'Create Quote'}
            </h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            {standalone && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                {selectedCustomer ? (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium">{selectedCustomer.business_name}</div>
                      <div className="text-sm text-gray-600">{selectedCustomer.contact_name}</div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedCustomer(null);
                        setQuoteData({...quoteData, userId: ''});
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <CustomerSearch
                    onSelect={(customer) => {
                      setSelectedCustomer(customer);
                      setQuoteData({...quoteData, userId: customer.id});
                    }}
                  />
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quote Title</label>
              <input
                type="text"
                value={quoteData.title}
                onChange={(e) => setQuoteData({...quoteData, title: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="e.g., Spring Garden Plants Quote"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Items</label>
                <button
                  onClick={addItem}
                  className="text-green-600 hover:text-green-700 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Add Item
                </button>
              </div>

              <div className="space-y-2">
                {quoteData.items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <input
                      type="text"
                      placeholder="Item name"
                      value={item.name}
                      onChange={(e) => updateItem(index, 'name', e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-lg"
                    />
                    <input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      className="w-20 px-3 py-2 border rounded-lg"
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      value={item.unitPrice}
                      onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                      className="w-24 px-3 py-2 border rounded-lg"
                    />
                    <label className="flex items-center gap-2 px-3 py-2 border rounded-lg whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={item.vatExempt}
                        onChange={(e) => updateItem(index, 'vatExempt', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">No VAT</span>
                    </label>
                    <button
                      onClick={() => removeItem(index)}
                      className="text-red-500 hover:text-red-700 p-2"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>£{totals.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>VAT (20%):</span>
                  <span>£{totals.vat.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total:</span>
                  <span>£{totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !quoteData.userId}
                className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Creating...' : 'Create & Send Quote'}
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Invoice Modal with VAT handling
  const InvoiceModal = ({ users, onClose, onSuccess }) => {
    const [invoiceData, setInvoiceData] = useState({
      userId: '',
      title: '',
      items: [{ name: '', quantity: 1, unitPrice: 0, vatExempt: false }],
      notes: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const addItem = () => {
      setInvoiceData({
        ...invoiceData,
        items: [...invoiceData.items, { name: '', quantity: 1, unitPrice: 0, vatExempt: false }]
      });
    };

    const updateItem = (index, field, value) => {
      const newItems = [...invoiceData.items];
      if (field === 'vatExempt') {
        newItems[index][field] = value;
      } else {
        newItems[index][field] = field === 'quantity' || field === 'unitPrice' ? parseFloat(value) || 0 : value;
      }
      setInvoiceData({ ...invoiceData, items: newItems });
    };

    const removeItem = (index) => {
      setInvoiceData({
        ...invoiceData,
        items: invoiceData.items.filter((_, i) => i !== index)
      });
    };

    const calculateTotal = () => {
      let subtotal = 0;
      let vat = 0;
      
      invoiceData.items.forEach(item => {
        const itemTotal = parseFloat(item.quantity) * parseFloat(item.unitPrice || 0);
        subtotal += itemTotal;
        if (!item.vatExempt) {
          vat += itemTotal * 0.20;
        }
      });
      
      return { subtotal, vat, total: subtotal + vat };
    };

    const handleSubmit = async () => {
      const token = localStorage.getItem('adminToken');
      setIsSubmitting(true);
      
      try {
        const response = await fetch(`${API_URL}/admin/invoices/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(invoiceData)
        });

        if (response.ok) {
          alert('Invoice created and sent successfully!');
          onSuccess();
        } else {
          const errorData = await response.json();
          alert(errorData.error || 'Failed to create invoice');
        }
      } catch (error) {
        console.error('Error creating invoice:', error);
        alert('Failed to create invoice');
      } finally {
        setIsSubmitting(false);
      }
    };

    const totals = calculateTotal();

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold">Create Invoice</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
              {selectedCustomer ? (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium">{selectedCustomer.business_name}</div>
                    <div className="text-sm text-gray-600">{selectedCustomer.contact_name}</div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedCustomer(null);
                      setInvoiceData({...invoiceData, userId: ''});
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <CustomerSearch
                  onSelect={(customer) => {
                    setSelectedCustomer(customer);
                    setInvoiceData({...invoiceData, userId: customer.id});
                  }}
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Title</label>
              <input
                type="text"
                value={invoiceData.title}
                onChange={(e) => setInvoiceData({...invoiceData, title: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Items</label>
                <button
                  onClick={addItem}
                  className="text-green-600 hover:text-green-700 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Add Item
                </button>
              </div>

              <div className="space-y-2">
                {invoiceData.items.map((item, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Item name"
                      value={item.name}
                      onChange={(e) => updateItem(index, 'name', e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-lg"
                    />
                    <input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      className="w-20 px-3 py-2 border rounded-lg"
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      value={item.unitPrice}
                      onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                      className="w-24 px-3 py-2 border rounded-lg"
                    />
                    <label className="flex items-center gap-2 px-3 py-2 border rounded-lg whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={item.vatExempt}
                        onChange={(e) => updateItem(index, 'vatExempt', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">No VAT</span>
                    </label>
                    <button
                      onClick={() => removeItem(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>£{totals.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>VAT (20%):</span>
                  <span>£{totals.vat.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total:</span>
                  <span>£{totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !invoiceData.userId}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Creating...' : 'Create & Send Invoice'}
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Edit Quote Modal
  const EditQuoteModal = ({ quote, onClose, onSuccess }) => {
    const [quoteData, setQuoteData] = useState({
      title: quote.title || '',
      items: quote.items ? quote.items.map(item => ({
        name: item.item_name,
        quantity: item.quantity,
        unitPrice: parseFloat(item.unit_price),
        vatExempt: item.vat_exempt || false
      })) : [{ name: '', quantity: 1, unitPrice: 0, vatExempt: false }],
      status: quote.status || 'pending_signature'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const addItem = () => {
      setQuoteData({
        ...quoteData,
        items: [...quoteData.items, { name: '', quantity: 1, unitPrice: 0, vatExempt: false }]
      });
    };

    const updateItem = (index, field, value) => {
      const newItems = [...quoteData.items];
      if (field === 'vatExempt') {
        newItems[index][field] = value;
      } else {
        newItems[index][field] = field === 'quantity' || field === 'unitPrice' ? parseFloat(value) || 0 : value;
      }
      setQuoteData({ ...quoteData, items: newItems });
    };

    const removeItem = (index) => {
      setQuoteData({
        ...quoteData,
        items: quoteData.items.filter((_, i) => i !== index)
      });
    };

    const calculateTotal = () => {
      let subtotal = 0;
      let vat = 0;
      
      quoteData.items.forEach(item => {
        const itemTotal = parseFloat(item.quantity) * parseFloat(item.unitPrice || 0);
        subtotal += itemTotal;
        if (!item.vatExempt) {
          vat += itemTotal * 0.20;
        }
      });
      
      return { subtotal, vat, total: subtotal + vat };
    };

    const handleSubmit = async () => {
      const token = localStorage.getItem('adminToken');
      setIsSubmitting(true);

      try {
        const response = await fetch(`${API_URL}/admin/quotes/${quote.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(quoteData)
        });

        if (response.ok) {
          alert('Quote updated successfully!');
          onSuccess();
        } else {
          const errorData = await response.json();
          alert(errorData.error || 'Failed to update quote');
        }
      } catch (error) {
        console.error('Error updating quote:', error);
        alert('Failed to update quote');
      } finally {
        setIsSubmitting(false);
      }
    };

    const totals = calculateTotal();

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold">Edit Quote #{quote.id}</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quote Title</label>
              <input
                type="text"
                value={quoteData.title}
                onChange={(e) => setQuoteData({...quoteData, title: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={quoteData.status}
                onChange={(e) => setQuoteData({...quoteData, status: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="pending_signature">Pending Signature</option>
                <option value="signed">Signed</option>
              </select>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Items</label>
                <button
                  onClick={addItem}
                  className="text-green-600 hover:text-green-700 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Add Item
                </button>
              </div>

              <div className="space-y-2">
                {quoteData.items.map((item, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Item name"
                      value={item.name}
                      onChange={(e) => updateItem(index, 'name', e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-lg"
                    />
                    <input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      className="w-20 px-3 py-2 border rounded-lg"
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      value={item.unitPrice}
                      onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                      className="w-24 px-3 py-2 border rounded-lg"
                    />
                    <label className="flex items-center gap-2 px-3 py-2 border rounded-lg whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={item.vatExempt}
                        onChange={(e) => updateItem(index, 'vatExempt', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">No VAT</span>
                    </label>
                    <button
                      onClick={() => removeItem(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>£{totals.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>VAT (20%):</span>
                  <span>£{totals.vat.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total:</span>
                  <span>£{totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isSubmitting ? 'Updating...' : 'Update Quote'}
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Admin Login Component
  const AdminLogin = () => {
    const [credentials, setCredentials] = useState({ email: '', password: '' });

    const handleLogin = async () => {
      try {
        const response = await fetch(`${API_URL}/admin/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(credentials)
        });
        
        const data = await response.json();
        if (data.token) {
          localStorage.setItem('adminToken', data.token);
          setAdminUser(data.admin);
          setCurrentView('dashboard');
          fetchDashboardData();
        }
      } catch (error) {
        console.error('Login error:', error);
        alert('Login failed');
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-2xl">N</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Admin Portal</h2>
            <p className="text-gray-600">Newgate Nurseries Staff Login</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={credentials.email}
                onChange={(e) => setCredentials({...credentials, email: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="admin@newgatenurseries.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="••••••••"
              />
            </div>
            <button
              onClick={handleLogin}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-lg hover:from-green-700 hover:to-emerald-700"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Admin Dashboard
  const AdminDashboard = () => {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-xl">N</span>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Admin Portal</h1>
                  <p className="text-sm text-gray-600">Newgate Nurseries</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">Welcome, {adminUser?.name}</span>
                <button
                  onClick={() => {
                    localStorage.removeItem('adminToken');
                    setCurrentView('login');
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalUsers || 0}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pending Enquiries</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.enquiries?.pending || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Quotes</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.quotes?.pending || 0}</p>
                </div>
                <FileText className="w-8 h-8 text-green-500" />
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Customers Owing</p>
                  <p className="text-2xl font-bold text-gray-900">{invoiceSummary?.count || 0}</p>
                </div>
                <CreditCard className="w-8 h-8 text-red-500" />
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Outstanding</p>
                  <p className="text-2xl font-bold text-gray-900">
                    £{(invoiceSummary?.totalAmount || 0).toLocaleString()}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-purple-500" />
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-sm border">
            <div className="border-b border-gray-200 overflow-x-auto">
              <div className="flex space-x-8 px-6">
                {['dashboard', 'users', 'enquiries', 'quotes', 'invoices', 'accounting', 'customers', 'payments', 'bulk-email'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-2 border-b-2 font-medium text-sm capitalize whitespace-nowrap ${
                      activeTab === tab
                        ? 'border-green-500 text-green-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">
              {/* Dashboard Tab */}
              {activeTab === 'dashboard' && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
                  <div className="space-y-3">
                    {stats.recentActivity?.map((activity, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <MessageSquare className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="font-medium">{activity.title}</p>
                            <p className="text-sm text-gray-600">by {activity.user_name}</p>
                          </div>
                        </div>
                        <span className="text-sm text-gray-500">
                          {new Date(activity.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    )) || <p className="text-gray-500 text-center py-8">No recent activity</p>}
                  </div>
                </div>
              )}

              {/* Users Tab */}
              {activeTab === 'users' && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Registered Users</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Business</th>
                          <th className="text-left py-2">Contact</th>
                          <th className="text-left py-2">Email</th>
                          <th className="text-center py-2">Enquiries</th>
                          <th className="text-center py-2">Quotes</th>
                          <th className="text-left py-2">Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr key={user.id} className="border-b hover:bg-gray-50">
                            <td className="py-3">{user.business_name}</td>
                            <td className="py-3">{user.contact_name}</td>
                            <td className="py-3">{user.email}</td>
                            <td className="py-3 text-center">{user.enquiry_count}</td>
                            <td className="py-3 text-center">{user.quote_count}</td>
                            <td className="py-3">{new Date(user.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Enquiries Tab */}
              {activeTab === 'enquiries' && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Customer Enquiries</h3>
                  <div className="space-y-4">
                    {enquiries.map((enquiry) => (
                      <div key={enquiry.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold">{enquiry.title}</h4>
                            <p className="text-sm text-gray-600">
                              From: {enquiry.business_name} ({enquiry.contact_name})
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            enquiry.status === 'pending' 
                              ? 'bg-yellow-100 text-yellow-800'
                              : enquiry.status === 'quoted'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {enquiry.status}
                          </span>
                        </div>
                        
                        <p className="text-gray-700 mb-3">{enquiry.description}</p>
                        
                        {enquiry.delivery_required && (
                          <div className="bg-blue-50 p-3 rounded border-l-4 border-blue-400 mb-3">
                            <p className="text-sm font-medium text-blue-800">🚛 Delivery Required</p>
                            <p className="text-blue-700">{enquiry.delivery_address}</p>
                          </div>
                        )}
                        
                        {enquiry.response && (
                          <div className="bg-green-50 p-3 rounded border-l-4 border-green-400 mb-3">
                            <p className="text-sm font-medium text-green-800">Response sent:</p>
                            <p className="text-green-700">{enquiry.response}</p>
                          </div>
                        )}

                        {respondingTo === enquiry.id ? (
                          <div className="space-y-3">
                            <textarea
                              value={responseText}
                              onChange={(e) => setResponseText(e.target.value)}
                              className="w-full px-3 py-2 border rounded-lg"
                              rows={3}
                              placeholder="Type your response..."
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleRespond(enquiry.id)}
                                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                              >
                                Send Response
                              </button>
                              <button
                                onClick={() => {
                                  setRespondingTo(null);
                                  setResponseText('');
                                }}
                                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            {enquiry.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => setRespondingTo(enquiry.id)}
                                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                                >
                                  <MessageSquare className="w-4 h-4" /> Respond
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedEnquiry(enquiry);
                                    setShowQuoteModal(true);
                                  }}
                                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                                >
                                  <FileText className="w-4 h-4" /> Create Quote
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quotes Tab */}
              {activeTab === 'quotes' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold">Quotes</h3>
                    <button
                      onClick={() => setShowStandaloneQuoteModal(true)}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Create Quote
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    {quotes.map((quote) => (
                      <div key={quote.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold">{quote.title}</h4>
                            <p className="text-sm text-gray-600">
                              For: {quote.business_name} ({quote.contact_name})
                            </p>
                            {quote.enquiry_id ? (
                              <p className="text-xs text-blue-600">From Enquiry #{quote.enquiry_id}</p>
                            ) : (
                              <p className="text-xs text-green-600">Standalone Quote</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setEditingQuote(quote)}
                              className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 flex items-center gap-1"
                            >
                              <Edit2 className="w-3 h-3" />
                              Edit
                            </button>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              quote.status === 'pending_signature'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {quote.status === 'pending_signature' ? 'Awaiting Signature' : 'Signed'}
                            </span>
                            {quote.status === 'signed' && quote.payment_status && (
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                quote.payment_status === 'paid'
                                  ? 'bg-green-100 text-green-800'
                                  : quote.payment_status === 'partial'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {quote.payment_status === 'paid' ? 'Paid' : quote.payment_status === 'partial' ? 'Partial' : 'Unpaid'}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="bg-gray-50 rounded p-3 mb-3">
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Subtotal:</span>
                              <span className="font-medium ml-2">£{quote.subtotal}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">VAT:</span>
                              <span className="font-medium ml-2">£{quote.vat}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Total:</span>
                              <span className="font-bold ml-2">£{quote.total}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          {quote.signed_date && (
                            <p className="text-sm text-green-600 flex items-center">
                              <Check className="w-4 h-4 inline mr-1" />
                              Signed on {new Date(quote.signed_date).toLocaleDateString()}
                            </p>
                          )}
                          {quote.status === 'signed' && (
                            <>
                              <button
                                onClick={() => resendInvoice(quote.id)}
                                className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm"
                              >
                                <RefreshCw className="w-3 h-3" />
                                Resend
                              </button>
                              {quote.payment_status !== 'paid' && (
                                <button
                                  onClick={() => markAsPaid(quote.id)}
                                  className="bg-green-600 text-white px-4 py-1 rounded text-sm hover:bg-green-700"
                                >
                                  Mark as Paid
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Invoices Tab */}
              {activeTab === 'invoices' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold">Invoice Management</h3>
                    <button
                      onClick={() => setShowInvoiceModal(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4" />
                      Create Invoice
                    </button>
                  </div>

                  <div className="space-y-4">
                    {quotes.filter(q => q.status === 'signed').map((invoice) => (
                      <div key={invoice.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold">Invoice #{invoice.id}</h4>
                            <p className="text-sm text-gray-600">
                              For: {invoice.business_name} ({invoice.contact_name})
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Invoice
                            </span>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              invoice.payment_status === 'paid'
                                ? 'bg-green-100 text-green-800'
                                : invoice.payment_status === 'partial'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {invoice.payment_status === 'paid' ? 'Paid' : invoice.payment_status === 'partial' ? 'Partial' : 'Unpaid'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="bg-gray-50 rounded p-3 mb-3">
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Subtotal:</span>
                              <span className="font-medium ml-2">£{invoice.subtotal}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">VAT:</span>
                              <span className="font-medium ml-2">£{invoice.vat}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Total:</span>
                              <span className="font-bold ml-2">£{invoice.total}</span>
                            </div>
                          </div>
                        </div>

                        <div className="text-sm text-gray-600">
                          Created: {new Date(invoice.signed_date).toLocaleDateString()}
                          {invoice.payment_date && (
                            <span className="ml-4">
                              Paid: {new Date(invoice.payment_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Accounting Tab */}
              {activeTab === 'accounting' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold">Accounting Dashboard</h3>
                    <button
                      onClick={printUnpaidSummary}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <Printer className="w-4 h-4" />
                      Print Summary
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-red-50 p-6 rounded-xl border border-red-200">
                      <h4 className="font-semibold text-red-800 mb-2">Customers with Outstanding Balance</h4>
                      <p className="text-3xl font-bold text-red-600">{invoiceSummary.count}</p>
                      <p className="text-red-700 text-sm mt-1">{invoiceSummary.totalInvoices || 0} total invoices</p>
                    </div>
                    <div className="bg-orange-50 p-6 rounded-xl border border-orange-200">
                      <h4 className="font-semibold text-orange-800 mb-2">Total Outstanding</h4>
                      <p className="text-3xl font-bold text-orange-600">£{(invoiceSummary?.totalAmount || 0).toFixed(2)}</p>
                      <p className="text-orange-700 text-sm mt-1">Across all customers</p>
                    </div>
                    <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                      <h4 className="font-semibold text-green-800 mb-2">Paid This Month</h4>
                      <p className="text-3xl font-bold text-green-600">
                        {quotes.filter(q => q.payment_status === 'paid' && 
                          q.payment_date && new Date(q.payment_date).getMonth() === new Date().getMonth()).length}
                      </p>
                      <p className="text-green-700 text-sm mt-1">
                        £{quotes
                          .filter(q => q.payment_status === 'paid' && 
                            q.payment_date && new Date(q.payment_date).getMonth() === new Date().getMonth())
                          .reduce((sum, q) => sum + parseFloat(q.total), 0)
                          .toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold mb-4">Customer Accounts - Outstanding Balances</h4>
                    {unpaidInvoices.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b bg-gray-50">
                              <th className="text-left py-3 px-4">Customer</th>
                              <th className="text-left py-3 px-4">Contact</th>
                              <th className="text-center py-3 px-4">Invoices</th>
                              <th className="text-right py-3 px-4">Total Invoiced</th>
                              <th className="text-right py-3 px-4">Total Paid</th>
                              <th className="text-right py-3 px-4">Outstanding</th>
                              <th className="text-center py-3 px-4">Days</th>
                              <th className="text-center py-3 px-4">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {unpaidInvoices.map((customer) => {
                              const totalInvoiced = customer.total_invoiced || 0;
                              const outstanding = customer.outstanding || 0;
                              const totalPaid = customer.total_paid !== undefined ? customer.total_paid : (totalInvoiced - outstanding);
                              return (
                              <tr key={customer.customer_id} className="border-b hover:bg-gray-50">
                                <td className="py-3 px-4">
                                  <div className="font-medium">{customer.business_name}</div>
                                  <div className="text-xs text-gray-500">{customer.email}</div>
                                </td>
                                <td className="py-3 px-4">{customer.contact_name}</td>
                                <td className="py-3 px-4 text-center">
                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                    {customer.invoice_count || 0}
                                  </span>
                                </td>
                                <td className="py-3 px-4 text-right">£{totalInvoiced.toFixed(2)}</td>
                                <td className="py-3 px-4 text-right text-green-600">£{totalPaid.toFixed(2)}</td>
                                <td className="py-3 px-4 text-right">
                                  <span className={`font-bold ${outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    £{outstanding.toFixed(2)}
                                  </span>
                                </td>
                                <td className={`py-3 px-4 text-center ${(customer.days_outstanding || 0) > 30 ? 'text-red-600 font-bold' : ''}`}>
                                  {customer.days_outstanding || 0}
                                </td>
                                <td className="py-3 px-4 text-center">
                                  <div className="flex gap-2 justify-center">
                                    <button
                                      onClick={() => {
                                        const user = users.find(u => u.id === customer.customer_id);
                                        if (user) {
                                          setSelectedCustomerForStatement(user);
                                          setShowAccountSummaryModal(true);
                                        }
                                      }}
                                      className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700"
                                      title="View Account Details"
                                    >
                                      <Eye className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        const user = users.find(u => u.id === customer.customer_id);
                                        if (user) {
                                          setSelectedCustomerForPayment(user);
                                          setShowPaymentModal(true);
                                        }
                                      }}
                                      className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700"
                                      title="Record Payment"
                                    >
                                      <DollarSign className="w-3 h-3" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="bg-gray-50 border-t-2">
                            <tr>
                              <td colSpan="5" className="py-3 px-4 font-bold text-right">TOTAL OUTSTANDING:</td>
                              <td className="py-3 px-4 text-right font-bold text-red-600 text-lg">
                                £{(invoiceSummary?.totalAmount || 0).toFixed(2)}
                              </td>
                              <td colSpan="2"></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <CreditCard className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p className="text-gray-600 font-medium">No outstanding balances</p>
                        <p className="text-gray-500 text-sm mt-1">All customers are up to date with payments!</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Customers Tab */}
              {activeTab === 'customers' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold">Customer Management</h3>
                    <button
                      onClick={() => setShowCustomerModal(true)}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Customer
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Business</th>
                          <th className="text-left py-2">Contact</th>
                          <th className="text-left py-2">Email</th>
                          <th className="text-center py-2">Enquiries</th>
                          <th className="text-center py-2">Quotes</th>
                          <th className="text-left py-2">Joined</th>
                          <th className="text-center py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr key={user.id} className="border-b hover:bg-gray-50">
                            <td className="py-3">{user.business_name}</td>
                            <td className="py-3">{user.contact_name}</td>
                            <td className="py-3">{user.email}</td>
                            <td className="py-3 text-center">{user.enquiry_count}</td>
                            <td className="py-3 text-center">{user.quote_count}</td>
                            <td className="py-3">{new Date(user.created_at).toLocaleDateString()}</td>
                            <td className="py-3 text-center">
                              <div className="flex gap-2 justify-center">
                                <button
                                  onClick={() => setEditingCustomer(user)}
                                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 flex items-center gap-1"
                                >
                                  <Edit2 className="w-3 h-3" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedCustomerForStatement(user);
                                    setShowAccountSummaryModal(true);
                                  }}
                                  className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                                >
                                  Account
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedCustomerForPayment(user);
                                    setShowPaymentModal(true);
                                  }}
                                  className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                                >
                                  Payment
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Payments Tab */}
              {activeTab === 'payments' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold">Payment Management</h3>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search Customer to Record Payment
                    </label>
                    <CustomerSearch
                      onSelect={(customer) => {
                        setSelectedCustomerForPayment(customer);
                        setShowPaymentModal(true);
                      }}
                    />
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-blue-800">
                      Use the search above to find a customer and record a payment against their invoices.
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 'bulk-email' && (
                <BulkEmailTab />
              )}
            </div>
          </div>
        </div>

        {/* Modals */}
        {showCustomerModal && (
          <CustomerModal
            onClose={() => setShowCustomerModal(false)}
            onSuccess={() => {
              fetchAllData();
              setShowCustomerModal(false);
            }}
          />
        )}

        {showInvoiceModal && (
          <InvoiceModal
            users={users}
            onClose={() => setShowInvoiceModal(false)}
            onSuccess={() => {
              fetchAllData();
              setShowInvoiceModal(false);
            }}
          />
        )}

        {showQuoteModal && selectedEnquiry && (
          <EnhancedQuoteModal
            enquiry={selectedEnquiry}
            users={users}
            onClose={() => {
              setShowQuoteModal(false);
              setSelectedEnquiry(null);
            }}
            onSuccess={() => {
              fetchAllData();
              setShowQuoteModal(false);
              setSelectedEnquiry(null);
            }}
          />
        )}

        {showStandaloneQuoteModal && (
          <EnhancedQuoteModal
            users={users}
            standalone={true}
            onClose={() => setShowStandaloneQuoteModal(false)}
            onSuccess={() => {
              fetchAllData();
              setShowStandaloneQuoteModal(false);
            }}
          />
        )}

        {showPaymentModal && selectedCustomerForPayment && (
          <PaymentModal
            customer={selectedCustomerForPayment}
            onClose={() => {
              setShowPaymentModal(false);
              setSelectedCustomerForPayment(null);
            }}
            onSuccess={() => {
              fetchAllData();
              setShowPaymentModal(false);
              setSelectedCustomerForPayment(null);
            }}
          />
        )}

        {showAccountSummaryModal && selectedCustomerForStatement && (
          <AccountSummaryModal
            customer={selectedCustomerForStatement}
            onClose={() => {
              setShowAccountSummaryModal(false);
              setSelectedCustomerForStatement(null);
            }}
          />
        )}

        {editingCustomer && (
          <EditCustomerModal
            customer={editingCustomer}
            onClose={() => setEditingCustomer(null)}
            onSuccess={() => {
              fetchAllData();
              setEditingCustomer(null);
            }}
          />
        )}

        {editingQuote && (
          <EditQuoteModal
            quote={editingQuote}
            onClose={() => setEditingQuote(null)}
            onSuccess={() => {
              fetchAllData();
              setEditingQuote(null);
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      {currentView === 'login' ? <AdminLogin /> : <AdminDashboard />}
    </div>
  );
};

export default AdminPanel;